<template>
    <div class="card-container">
        <div class="card-wrapper" :class="{ 'card-wrapper--list': displayType === 'list' }">
            <a
                :href="offer.detail_link"
                class="card h-100 text-decoration-none position-relative"
                :style="offer.car.status === 'reserved' ? 'opacity:0.5;' : ''"
                @click="handleCardClick">

                <!-- Carousel -->
                <div class="card-image mb-2">

                    <div class="card-status-bar">
                        <span
                            v-for="(b, i) in badges"
                            :key="i"
                            class="badge"
                            :class="[b.class, { 'volvo-selekt-tooltip-trigger': b.class === 'badge--select' }]"
                            @click.stop.prevent="b.class === 'badge--select' ? showVolvoSelectTooltip($event) : null"
                        >
                            <span class="text-uppercase badge-text">{{ b.text }}</span>
                            <!-- Add tooltip for Volvo Select badge -->
                            <template v-if="b.class === 'badge--select'">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" class="ms-1 bi bi-info-circle" viewBox="0 0 16 16">
                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                                </svg>
                                <div class="volvo-selekt-tooltip-content" v-html="volvoSelektTooltip"></div>
                            </template>
                        </span>
                        <span v-if="offer.car.custom_tag" :key="offer.car.id + '-custom-tag'" class="badge badge--custom">
                            <span class="text-uppercase badge-text">{{ offer.car.custom_tag }}</span>
                        </span>
                        <span v-if="offer.car.custom_tag_2" :key="offer.car.id + '-custom-tag-2'" class="badge badge--custom" style="background: #00C8FF;">
                            <span class="text-uppercase badge-text">{{ offer.car.custom_tag_2 }}</span>
                        </span>
                        <!-- Add custom tags from car.tags where type is custom -->
                        <span
                            v-for="tag in customTags"
                            :key="tag.key"
                            class="badge"
                            :class="tag.classes"
                            :style="tag.style"
                        >
                            <span class="text-uppercase badge-text">{{ tag.title }}</span>
                        </span>
                    </div>

                    <div v-if="displayType !== 'list'" class="card-actions">
                        <!-- Porovnání -->
                        <button @click.stop.prevent="toggleCompare" :class="['btn-icon', 'btn-icon--compare', { active: isCompared }]" :title="isCompared ? 'Zrušit porovnání' : 'Porovnat'"></button>

                        <!-- Oblíbené -->
                        <button @click.stop.prevent="toggleFavorite" :class="['btn-icon', 'btn-icon--like', { active: isFavorite }]" :title="isFavorite ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'"></button>
                    </div>

                    <!-- Single image display when there's only one image -->
                    <div v-if="images.length === 1" class="single-image-container">
                        <img
                            :src="images[0].box_path"
                            class="card-img-top single-image"
                            loading="lazy"
                            :alt="offer.car.name"
                        />
                    </div>

                    <!-- Carousel for multiple images -->
                    <div
                        v-else-if="images.length > 1"
                        class="carousel-container"
                        @touchstart="handleTouchStart"
                        @touchmove="handleTouchMove"
                        @touchend="handleTouchEnd"
                        @mousedown="handleMouseDown"
                        @mousemove="handleMouseMove"
                        @mouseup="handleMouseUp"
                        @mouseleave="handleMouseLeave"
                    >
                        <div
                            class="carousel-images-wrapper"
                            :style="{
                                transform: `translateX(${dragOffset}px)`,
                                transition: isDragging ? 'none' : 'transform 0.3s ease',
                                willChange: 'transform' /* Optimize for animations */
                            }"
                        >
                            <!-- Previous image -->
                            <img
                                :src="images[getPrevIndex()].box_path"
                                class="card-img-top carousel-image prev-image"
                                loading="lazy"
                                :alt="`${offer.car.name} - ${getPrevIndex()+1}`"
                            />

                            <!-- Current image -->
                            <img
                                :src="images[currentIndex].box_path"
                                class="card-img-top carousel-image current-image"
                                loading="lazy"
                                :alt="`${offer.car.name} - ${currentIndex+1}`"
                            />

                            <!-- Next image -->
                            <img
                                :src="images[getNextIndex()].box_path"
                                class="card-img-top carousel-image next-image"
                                loading="lazy"
                                :alt="`${offer.car.name} - ${getNextIndex()+1}`"
                            />
                        </div>

                        <!-- Carousel navigation buttons -->
                        <button v-if="displayType !== 'list'" class="carousel__btn prev" @click.stop.prevent="prev"></button>
                        <button v-if="displayType !== 'list'" class="carousel__btn next" @click.stop.prevent="next"></button>

                        <!-- Carousel indicators -->
                        <div class="carousel__indicators">
                            <span
                                v-for="(image, index) in images"
                                :key="index"
                                class="carousel__indicator"
                                :class="{ 'active': index === currentIndex }"
                                @click.stop.prevent="currentIndex = index"
                            ></span>
                        </div>
                    </div>

                    <!-- Placeholder when no images -->
                    <img
                        v-else
                        :src="noImageUrl"
                        :alt="noImagesText"
                        class="card-img-top placeholder"
                        loading="lazy"
                    />
                </div>



                <!-- wrapper pro „více“ + menu -->
                <div v-if="displayType !== 'list'" class="more-wrapper">
                    <button class="btn-icon--more" @click.stop.prevent="$emit('toggle-dropdown', offer.id)">
                        <!-- tři tečky SVG -->
                        <svg width="24" height="24" viewBox="0 0 24 24">
                            <circle cx="5"  cy="12" r="2"/>
                            <circle cx="12" cy="12" r="2"/>
                            <circle cx="19" cy="12" r="2"/>
                        </svg>
                    </button>

                    <ul v-if="isDropdownOpen" class="card-actions-dropdown-menu">
                        <li class="my-1">
                            <button @click.stop.prevent="toggleFavorite" class="dropdown-item">
                                <div class="d-flex align-items-center">
                                    <svg v-if="!isFavorite" class="icon me-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill="#fff" stroke="#80878B" stroke-width="2" d="M11.93 22.06s10.63-7.03 10.9-13.99c.02-.46.04-.71 0-1.17-.17-2.03-.71-3.42-2.3-4.66-1.46-1.15-2.67-1.39-4.59-1.17C14.03 1.3 11.93 4 11.93 4s-2.1-2.7-4.02-2.92C5.99.85 4.78 1.1 3.3 2.24 1.74 3.48 1.2 4.87 1.03 6.9c-.04.46-.02.71 0 1.17.27 6.96 10.9 13.99 10.9 13.99Z"/></svg>
                                    <svg v-else class="icon me-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill="#255592" stroke="#255592" stroke-width="2" d="M11.93 22.06s10.63-7.03 10.9-13.99c.02-.46.04-.71 0-1.17-.17-2.03-.71-3.42-2.3-4.66-1.46-1.15-2.67-1.39-4.59-1.17C14.03 1.3 11.93 4 11.93 4s-2.1-2.7-4.02-2.92C5.99.85 4.78 1.1 3.3 2.24 1.74 3.48 1.2 4.87 1.03 6.9c-.04.46-.02.71 0 1.17.27 6.96 10.9 13.99 10.9 13.99Z"/></svg>
                                    {{ isFavorite ? lblDropdownRemoveFromFavorites : lblDropdownAddToFavorites }}
                                </div>
                            </button>
                        </li>
                        <li class="my-1">
                            <button @click.stop.prevent="toggleCompare" class="dropdown-item">
                                <div class="d-flex align-items-center">
                                    <svg v-if="!isCompared" class="me-3 icon icon--w24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M9.74017 3.97664C9.74017 2.77046 8.42721 2.02289 7.38994 2.63847L2.29545 5.66182C1.27954 6.26472 1.27954 7.73528 2.29545 8.33818L7.38994 11.3615L7.89288 10.514L7.38994 11.3615C8.42721 11.9771 9.74017 11.2295 9.74017 10.0234V9.62367H14.4439C15.3033 9.62367 16 8.92699 16 8.06759V5.93241C16 5.07301 15.3033 4.37633 14.4439 4.37633L9.74017 4.37633V3.97664Z" fill="transparent" stroke="#80878B" stroke-width="2"/>
                                        <path d="M14.2598 20.0234C14.2598 21.2295 15.5728 21.9771 16.6101 21.3615L21.7045 18.3382C22.7205 17.7353 22.7205 16.2647 21.7045 15.6618L16.6101 12.6385L16.0997 13.4984L16.6101 12.6385C15.5728 12.0229 14.2598 12.7705 14.2598 13.9766V14.3763H9.55608C8.69668 14.3763 8 15.073 8 15.9324V18.0676C8 18.927 8.69668 19.6237 9.55608 19.6237H14.2598V20.0234Z" fill="transparent" stroke="#80878B" stroke-width="2"/>
                                    </svg>
                                    <svg v-else class="me-3 icon icon--w24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M9.74017 3.97664C9.74017 2.77046 8.42721 2.02289 7.38994 2.63847L2.29545 5.66182C1.27954 6.26472 1.27954 7.73528 2.29545 8.33818L7.38994 11.3615L7.89288 10.514L7.38994 11.3615C8.42721 11.9771 9.74017 11.2295 9.74017 10.0234V9.62367H14.4439C15.3033 9.62367 16 8.92699 16 8.06759V5.93241C16 5.07301 15.3033 4.37633 14.4439 4.37633L9.74017 4.37633V3.97664Z" fill="#255592" stroke="#255592" stroke-width="2"/>
                                        <path d="M14.2598 20.0234C14.2598 21.2295 15.5728 21.9771 16.6101 21.3615L21.7045 18.3382C22.7205 17.7353 22.7205 16.2647 21.7045 15.6618L16.6101 12.6385L16.0997 13.4984L16.6101 12.6385C15.5728 12.0229 14.2598 12.7705 14.2598 13.9766V14.3763H9.55608C8.69668 14.3763 8 15.073 8 15.9324V18.0676C8 18.927 8.69668 19.6237 9.55608 19.6237H14.2598V20.0234Z" fill="#255592" stroke="#255592" stroke-width="2"/>
                                    </svg>
                                    {{ isCompared ? lblDropdownRemoveFromComparison : lblDropdownAddToComparison }}
                                </div>
                            </button>
                        </li>
                        <li class="my-1">
                            <button @click.stop.prevent="copyLink" class="dropdown-item">
                                <div class="d-flex align-items-center">
                                    <svg class="icon me-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                        <path stroke="#80878B" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
                                    </svg>
                                    {{ lblShare }}
                                </div>
                            </button>
                        </li>
                        <li class="my-1">
                            <button @click.stop.prevent="goToDetails" class="dropdown-item">
                                <div class="d-flex align-items-center">
                                    <svg class="icon icon--detail me-3" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.92 5.01C18.72 4.42 18.16 4 17.5 4H6.5C5.84 4 5.29 4.42 5.08 5.01L3 11V19C3 19.55 3.45 20 4 20H5C5.55 20 6 19.55 6 19V18H18V19C18 19.55 18.45 20 19 20H20C20.55 20 21 19.55 21 19V11L18.92 5.01ZM6.5 15C5.67 15 5 14.33 5 13.5C5 12.67 5.67 12 6.5 12C7.33 12 8 12.67 8 13.5C8 14.33 7.33 15 6.5 15ZM17.5 15C16.67 15 16 14.33 16 13.5C16 12.67 16.67 12 17.5 12C18.33 12 19 12.67 19 13.5C19 14.33 18.33 15 17.5 15ZM5 10L6.5 5.5H17.5L19 10H5Z" fill="#80878B"/>
                                    </svg>
                                    {{ lblDetails }}
                                </div>
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- Body -->
                <div class="card-body">
                    <!-- Grid view layout -->
                    <template v-if="displayType !== 'list'">
                        <!-- Vehicle name -->
                        <h2 class="card-title h5 ellipsis-1 mb-2"
                            :title="offer.car.year_of_manufacture + ' ' + offer.car.name">
                            <span class="detail--title--brand text--grey-dark me-1" v-if="offer.car.car_brand">{{ offer.car.car_brand.name }}</span>
                            <span class="detail--title--model text--grey-dark me-1" v-if="offer.car.car_model">{{ offer.car.car_model.name }}</span>
                            <span class="detail--title--variant text--400">{{ offer.car.imported_name }}</span>
                        </h2>
                        <!-- Vehicle parameters -->
                        <div class="labels d-flex flex-nowrap gap-1 mt-auto overflow-hidden mb-2">
                            <span v-if="formattedMileage" class="label">{{ formattedMileage }}</span>
                            <span v-if="offer.car.year_of_manufacture" class="label">{{ offer.car.year_of_manufacture }}</span>
                            <span v-if="offer.car.engine_power_kw" class="label">{{ offer.car.engine_power_kw }} kW</span>
                            <span v-if="offer.car.fuel_type" class="label">{{ offer.car.fuel_type.name }}</span>
                            <span v-if="offer.car.transmission_type" class="label" :title="offer.car.transmission_type.name">{{ offer.car.transmission_type.name }}</span>
                        </div>
                    </template>

                    <!-- List view layout -->
                    <template v-else>
                        <!-- Vehicle name -->
                        <h2 class="card-title h4 ellipsis-1 mb-8px"
                            :title="offer.car.year_of_manufacture + ' ' + offer.car.name">
                            <span class="detail--title--brand pe-1 text--grey-dark" v-if="offer.car.car_brand">{{ offer.car.car_brand.name }}</span>
                            <span class="detail--title--model pe-1 text--grey-dark" v-if="offer.car.car_model">{{ offer.car.car_model.name }}</span>
                            <span class="detail--title--variant text--400">{{ offer.car.imported_name }}</span>
                        </h2>
                        <!-- Vehicle parameters -->
                        <div class="labels d-flex flex-nowrap gap-1 mt-auto mb-12px">
                            <span v-if="formattedMileage" class="label text--13px text--500 text-nowrap text--grey-dark">{{ formattedMileage }}</span>
                            <span v-if="offer.car.year_of_manufacture" class="label text--13px text--500 text-nowrap text--grey-dark">{{ offer.car.year_of_manufacture }}</span>
                            <span v-if="offer.car.engine_power_kw" class="label text--13px">{{ offer.car.engine_power_kw }} kW</span>
                            <span v-if="offer.car.fuel_type" class="label text--13px">{{ offer.car.fuel_type.name }}</span>
                            <span v-if="offer.car.transmission_type" class="label text--13px label--ellipsis me-0" :title="offer.car.transmission_type.name">{{ offer.car.transmission_type.name }}</span>
                        </div>
                    </template>

                    <!-- Price section -->
                    <div class="text--600 text--grey-dark text-nowrap">

                        <!-- loans only (no leasing) - show monthly incl. VAT if available -->
                        <template v-if="offer.availability_type.id === 4 && monthlyInclVat && offer.has_loans && !offer.has_leasings">
                            <div class="card__priceContainer d-flex align-items-end">
                                <span class="card__priceContainer-price text-nowrap h4 d-inline mb-0 me-1">
                                    {{ lblFrom }}
                                </span>
                                <span :class="displayType === 'list' ? 'h3 d-inline mb-0' : 'h4 d-inline mb-0'">
                                    {{ monthlyInclVat }}
                                </span>
                                <span class="card__priceContainer-price-2 text--semi-small text--grey ms-2 text--400">
                                    {{ lblMonthlyInclVat }}
                                </span>
                            </div>
                        </template>

                        <!-- leasing -->
                        <template v-else-if="offer.availability_type.id === 4">
                            <div class="card__priceContainer d-flex align-items-end">
                                <span class="card__priceContainer-price text-nowrap h4 d-inline mb-0 me-1">
                                    {{ lblFrom }}
                                </span>
                                <span :class="displayType === 'list' ? 'h3 d-inline mb-0' : 'h4 d-inline mb-0'">
                                    {{ monthlyInclVat }}
                                </span>
                                <span class="card__priceContainer-price-2 text--semi-small text--grey ms-2 text--400">
                                    {{ lblMonthlyExclVat }}
                                </span>
                            </div>
                            <!--                            <div v-if="monthlyInclVat" class="card__priceContainer d-flex align-items-end ps-5px">-->
                            <!--                                <small class="text&#45;&#45;350 text&#45;&#45;grey">-->
                            <!--                                    {{ monthlyInclVat }} {{ lblMonthlyInclVat }}-->
                            <!--                                </small>-->
                            <!--                            </div>-->
                        </template>

                        <!-- všechny ostatní -->
                        <template v-else>
                            <div class="card__priceContainer d-flex align-items-end ps-5px">
                                <span :class="displayType === 'list' ? 'card__priceContainer-price text-nowrap h3 d-inline mb-0' : 'card__priceContainer-price text-nowrap h4 d-inline mb-0'">
                                    {{ priceInclVat }}
                                </span>
                                <span v-if="priceExclVat" class="card__priceContainer-price-2 ms-2 text--350 text--grey text-nowrap">
                                    {{ priceExclVat }}
                                    <span class="">{{ lblExclVatSimple }}</span>
                                </span>
                            </div>
                        </template>
                    </div>

                    <!-- Buttons for list view -->
                    <div v-if="displayType === 'list'" class="d-flex align-items-center justify-content-between mt-3 mb-2">
                        <div class="d-flex align-items-center">
                            <!-- Save/Favorite button -->
                            <button @click.stop.prevent="toggleFavorite"
                                    class="text-nowrap btn btn-sm btn-outline-primary me-2"
                                    :class="{ active: isFavorite }">
                                <span>{{ isFavorite ? lblRemoveFromFavorites : lblAddToFavorites }}</span>
                                <svg v-if="!isFavorite" class="icon icon--like" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill="#fff" stroke="#255592" stroke-width="1.5" d="M11.93 22.06s10.63-7.03 10.9-13.99c.02-.46.04-.71 0-1.17-.17-2.03-.71-3.42-2.3-4.66-1.46-1.15-2.67-1.39-4.59-1.17C14.03 1.3 11.93 4 11.93 4s-2.1-2.7-4.02-2.92C5.99.85 4.78 1.1 3.3 2.24 1.74 3.48 1.2 4.87 1.03 6.9c-.04.46-.02.71 0 1.17.27 6.96 10.9 13.99 10.9 13.99Z"/></svg>
                                <svg v-else class="icon icon--like" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path fill="#FACC16" stroke="#255592" stroke-width="1.5" d="M11.93 22.06s10.63-7.03 10.9-13.99c.02-.46.04-.71 0-1.17-.17-2.03-.71-3.42-2.3-4.66-1.46-1.15-2.67-1.39-4.59-1.17C14.03 1.3 11.93 4 11.93 4s-2.1-2.7-4.02-2.92C5.99.85 4.78 1.1 3.3 2.24 1.74 3.48 1.2 4.87 1.03 6.9c-.04.46-.02.71 0 1.17.27 6.96 10.9 13.99 10.9 13.99Z"/></svg>
                            </button>

                            <!-- Compare button -->
                            <button @click.stop.prevent="toggleCompare"
                                    class="text-nowrap btn btn-sm btn-outline-primary me-2"
                                    :class="{ active: isCompared }">
                                <span>{{ isCompared ? lblRemoveFromComparison : lblAddToComparison }}</span>
                                <svg v-if="!isCompared" class="icon icon--w28 icon--compare" xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 28 24" fill="none">
                                    <path d="M10.0086 2.18112C10.0086 1.07621 8.72206 0.470484 7.87043 1.17444L2.04068 5.99331C1.40874 6.51568 1.40874 7.48432 2.04068 8.00669L7.87043 12.8256C8.72206 13.5295 10.0086 12.9238 10.0086 11.8189V10.1855H15.4439C16.1652 10.1855 16.75 9.60076 16.75 8.87943V5.12057C16.75 4.39924 16.1652 3.81449 15.4439 3.81449H10.0086V2.18112Z" fill="white" stroke="#255592" stroke-width="1.5"/>
                                    <path d="M17.9914 21.8189C17.9914 22.9238 19.2779 23.5295 20.1296 22.8256L25.9593 18.0067C26.5913 17.4843 26.5913 16.5157 25.9593 15.9933L20.1296 11.1744C19.2779 10.4705 17.9914 11.0762 17.9914 12.1811V13.8145H12.5561C11.8348 13.8145 11.25 14.3992 11.25 15.1206V18.8794C11.25 19.6008 11.8348 20.1855 12.5561 20.1855H17.9914V21.8189Z" fill="white" stroke="#255592" stroke-width="1.5"/>
                                </svg>
                                <svg v-else class="icon icon--w28 icon--compare" xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 28 24" fill="none">
                                    <path d="M10.0086 2.18112C10.0086 1.07621 8.72206 0.470484 7.87043 1.17444L2.04068 5.99331C1.40874 6.51568 1.40874 7.48432 2.04068 8.00669L7.87043 12.8256C8.72206 13.5295 10.0086 12.9238 10.0086 11.8189V10.1855H15.4439C16.1652 10.1855 16.75 9.60076 16.75 8.87943V5.12057C16.75 4.39924 16.1652 3.81449 15.4439 3.81449H10.0086V2.18112Z" fill="#FACC16" stroke="#255592" stroke-width="1.5"/>
                                    <path d="M17.9914 21.8189C17.9914 22.9238 19.2779 23.5295 20.1296 22.8256L25.9593 18.0067C26.5913 17.4843 26.5913 16.5157 25.9593 15.9933L20.1296 11.1744C19.2779 10.4705 17.9914 11.0762 17.9914 12.1811V13.8145H12.5561C11.8348 13.8145 11.25 14.3992 11.25 15.1206V18.8794C11.25 19.6008 11.8348 20.1855 12.5561 20.1855H17.9914V21.8189Z" fill="#FACC16" stroke="#255592" stroke-width="1.5"/>
                                </svg>
                            </button>

                            <!-- Share button -->
                            <button @click.stop.prevent="copyLink" class="text-nowrap btn btn-sm btn-outline-primary">
                                <span>{{ lblShare }}</span>
                                <svg class="icon icon--share" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M12 5.33325C13.1046 5.33325 14 4.43782 14 3.33325C14 2.22868 13.1046 1.33325 12 1.33325C10.8954 1.33325 10 2.22868 10 3.33325C10 4.43782 10.8954 5.33325 12 5.33325Z" stroke="#255592" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M4 10C5.10457 10 6 9.10457 6 8C6 6.89543 5.10457 6 4 6C2.89543 6 2 6.89543 2 8C2 9.10457 2.89543 10 4 10Z" stroke="#255592" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M12 14.6667C13.1046 14.6667 14 13.7713 14 12.6667C14 11.5622 13.1046 10.6667 12 10.6667C10.8954 10.6667 10 11.5622 10 12.6667C10 13.7713 10.8954 14.6667 12 14.6667Z" stroke="#255592" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M5.72656 9.00659L10.2799 11.6599" stroke="#255592" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M10.2732 4.33984L5.72656 6.99318" stroke="#255592" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>

                        <!-- Details button (right) -->
                        <button @click.stop.prevent="goToDetails" class="btn btn-circle">
                            <img src="https://www.volvista.cz/images/icons/ico-arrow.svg" alt="" class="icon icon--arrow">
                        </button>
                    </div>
                </div>
            </a>
        </div>
    </div>

    <!-- Volvo Selekt Modal (shown on mobile) -->
    <VolvoSelektModal
        :is-visible="isModalVisible"
        :tooltip-content="volvoSelektTooltip"
        :badge-text="badgeTranslations['volvo-select'] || 'Volvo Selekt'"
        @close="isModalVisible = false"
    />
</template>

<script setup>
import VolvoSelektModal from '@/components/CarFilter/VolvoSelektModal.vue'
import { useCarCard } from '@/composables/useCarCard'

const noImageUrl = '/images/no-image.png'

// definice eventů, které komponenta vyvolá
const emit = defineEmits([
    'add-to-comparison',
    'remove-from-comparison',
    'add-to-favorites',
    'remove-from-favorites',
    'toggle-dropdown',
    'car-selected',
])

// props
const props = defineProps({
    offer: { type: Object,  required: true },
    isCompared: { type: Boolean, default: false },
    isFavorite: { type: Boolean, default: false },
    isDropdownOpen:  { type: Boolean, default: false },
    displayType: { type: String, default: 'grid' },

    lblFrom:                            String,
    lblMonthlyInclVat:                  String,
    lblMonthlyExclVat:                  String,
    lblExclVatSimple:                   String,

    lblAddToFavorites:                  String,
    lblRemoveFromFavorites:             String,
    lblAddToComparison:                 String,
    lblRemoveFromComparison:            String,
    lblDropdownAddToFavorites:          String,
    lblDropdownRemoveFromFavorites:     String,
    lblDropdownAddToComparison:         String,
    lblDropdownRemoveFromComparison:    String,
    lblAddedToFavorites:                String,
    lblRemovedFromFavorites:            String,
    lblAddedToComparison:               String,
    lblRemovedFromComparison:           String,
    lblComparedCarsLimitReached:        String,
    lblShare:                           String,
    lblDetails:                         String,
    lblShow:                            String,
    lblMoreActions:                     String,
    noImagesText:                       String,

    // Add prop for Volvo Selekt tooltip content
    volvoSelektTooltip:      { type: String, default: '' },

    // Badge translations
    badgeTranslations:       { type: Object, default: () => ({}) },

    // Currency code for price formatting (e.g. 'CZK', 'EUR')
    currencyCode:            { type: String, default: 'CZK' },
})

// Use the composable for shared logic
const {
    // State
    currentIndex,
    activeTooltip,
    isModalVisible,
    touchStartX,
    touchStartY,
    touchEndX,
    isDragging,
    dragOffset,

    // Computed
    images,
    badges,
    customTags,
    formattedMileage,
    priceInclVat,
    priceExclVat,
    monthlyInclVat,
    monthlyExclVat,

    // Methods
    next,
    prev,
    getPrevIndex,
    getNextIndex,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleSwipe,
    toggleCompare,
    toggleFavorite,
    copyLink,
    fallbackCopyTextToClipboard,
    goToDetails,
    showVolvoSelectTooltip,
    handleDocumentClick,
} = useCarCard(props, emit)

function handleCardClick() {
    emit('car-selected', props.offer)
}
</script>

<style scoped>
.card-wrapper {
    position: relative;
    height: 100%;
}

.card-status-bar {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    display: flex;
    gap: 0.5rem;
    z-index: 10;
    flex-wrap: wrap;
    max-width: 270px;
}

.card-status-bar .badge {
    margin-right: 0;
}

.card-image {
    position: relative;
    width: 100%;
    height: 257px;
    overflow: hidden;
    border-radius: 12px 12px 0 0;
}

.card-image img {
    display: block;
    height: 100%;
    object-fit: cover;
}

.card-actions {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    z-index: 20;
}

.btn-icon {
    background: transparent;
    border: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
    padding: 0;
}
.btn-icon:hover {
    transform: rotate(8deg) scale(1.1);
}

.btn-icon--compare {
    background-image: url('/images/icons/ico-compare.svg');
    background-repeat: no-repeat;
    height: 32px;
    position: absolute;
    right: 46px;
    top: 0px;
    width: 32px;
    background-size: 100% 100%;
}

.btn-icon--compare.active {
    background-image: url('/images/icons/ico-compare-active.svg');
    background-repeat: no-repeat;
    background-size: 100% 100%;
}

.btn-icon--like {
    background-image: url('/images/icons/ico-like.svg');
    background-repeat: no-repeat;
    height: 26px;
    position: absolute;
    right: 11px;
    top: 3px;
    width: 26px;
    background-size: 100% 100%;
}

.btn-icon--like.active {
    background-image: url('/images/icons/ico-like-fill.svg');
    background-repeat: no-repeat;
    background-size: 100% 100%;
}

.card-body {
    padding: 0.5rem 1rem;
}

.carousel__btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255,255,255,0.8);
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 32px;
    height: 32px;
    font-size: 1.25rem;
    line-height: 1;
    padding: 0;
  /* hide arrows by default */
  opacity: 0;
  pointer-events: none; /* prevent clicking when hidden */
  transition: opacity 0.4s ease;
}

/* show on hover */
.carousel-container:hover .carousel__btn {
  opacity: 1;
  pointer-events: auto;
}

.carousel__btn.prev,
.carousel__btn.next {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  cursor: pointer;
}

.carousel__btn.prev {
  left: 8px;
  background-image: url('/images/icons/ico-chevron-left-white-shadow.svg');
}

.carousel__btn.next {
  right: 8px;
  background-image: url('/images/icons/ico-chevron-right-white-shadow.svg');
}

.carousel__btn.prev,
.carousel__btn.next {
  background-repeat: no-repeat;
  background-position: 100%;
  background-size: contain;
}

.single-image-container {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
}

.single-image {
    /* width: 100%; */
    width: 101%; /* Fix for slight overflow issue */
    height: 100%;
    object-fit: cover;
    display: block;
    border-bottom: 1px solid #E7E9EE;
}

.carousel-container {
    /* width: 100%; */
    width: 101%; /* Fix for slight overflow issue */
    height: 100%;
    touch-action: pan-y; /* Allow vertical scrolling but handle horizontal swipes ourselves */
    cursor: grab;
    overflow: hidden; /* Ensure content doesn't overflow during drag */
    position: relative;
  border-bottom: 1px solid #E7E9EE;
}

.carousel-container:active {
    cursor: grabbing;
}

.carousel-images-wrapper {
    display: flex;
    width: 300%; /* Increased to allow full visibility of each image */
    height: 100%;
    position: relative;
    left: -100%; /* Adjusted to center the current image */
}

.carousel-image {
    flex: 1;
    width: 33.333%; /* Each image takes up 1/3 of the wrapper */
    object-fit: cover;
    display: block;
}

.carousel__indicators {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 2px;
    z-index: 10;
}

.carousel__indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #edf1fa;
    cursor: pointer;
    transition: background-color 0.3s ease;
    scale: 0.5;
}

.carousel__indicator.active {
    background-color: #2950ad;
}

.card-actions-dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    padding: 0.25rem 0;
    list-style: none;
    z-index: 30;
}

.card-actions-dropdown-menu button {
    display: block;
    width: 100%;
    min-width: 240px;
    padding: 0.5rem 1rem;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
}

.card-actions-dropdown-menu button:hover {
    color: #255592;
}

/* List view styles */
.card-wrapper--list {
    height: 196px;
}
.card-wrapper--list .card {
    display: flex;
    flex-direction: row;
}

.card-wrapper--list .card-image {
    width: 300px;
    height: 196px;
    flex-shrink: 0;
    border-radius: 8px 0 0 8px;
    margin-bottom: 0;
}

.card-wrapper--list .card-image img {
    border-radius: 8px 0 0 8px;
}

.card-wrapper--list .card-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start; /* Changed from center to flex-start */
    padding: 1rem 1.5rem;
    min-width: 0; /* Fix for text overflow */
}

.card-wrapper--list .text-nowrap {
    text-wrap: wrap;
    white-space: normal;
}

.card-wrapper--list .more-wrapper {
    top: 0.5rem;
    right: 0.5rem;
}

.card-wrapper--list .labels {
    margin-bottom: 1rem;
}

.card-wrapper--list .card-title {
    line-height: 1;
    font-size: 25px;
    margin-bottom: 0.75rem;
}

.card-wrapper--list .card-body .ellipsis-1 {
    overflow: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Button styles for list view */
.btn-circle {
    border: 1px solid #e7e9ee;
    bottom: 20px;
    position: absolute;
    right: 24px;
    border-radius: 50%;
    height: 40px;
    justify-content: center;
    padding: 0;
    width: 40px;
}

.btn-circle:hover {
    background-color: #f5f5f5;
    border-color: #ccc;
}

.icon--arrow {
    width: 18px;
    height: 15px;
}

.icon--share {
    width: 16px;
    height: 16px;
}

.icon--compare {
    width: 24px;
    height: 20px;
}

.icon--like {
    width: 20px;
    height: 20px;
}

.btn-sm {
    width: auto;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    padding: 0 16px;
}

.btn-sm span {
    margin-bottom: -3px; /* Adjust for vertical alignment */
}

.btn-sm svg{
    margin-left: 8px;
}

.btn-outline-primary {
    color: #255592;
    border-color: #255592;
}

.btn-outline-primary:hover, .btn-outline-primary.active {
    color: #fff;
    background-color: #255592;
    border-color: #255592;
}

.btn-outline-primary.active svg path {
    fill: #FACC16;
    stroke: #255592;
}

.btn-outline-primary:hover svg path {
    stroke: #fff;
}

@media (max-width: 768px) {
    .card-wrapper--list .card {
        flex-direction: column;
    }

    .card-wrapper--list .card-image {
        width: 100%;
        border-radius: 8px 8px 0 0;
        height: 240px;
    }

    .card-wrapper--list .more-wrapper {
        top: 180px;
    }
}

/* Volvo Select tooltip styles */
.volvo-selekt-tooltip-content {
    display: none;
    position: absolute;
    top: 30px;
    left: -80px;
    white-space: normal;
}

.volvo-selekt-tooltip-content::after {
    content: '';
    position: absolute;
    top: -20px;
    left: 95px;
}


.more-wrapper {
  position: absolute;
  top: 235px;
  right: 0.5rem;
  z-index: 30;
  overflow: visible;        /* dropdown nesmí ořezávat rodič */
}

.btn-icon--more {
  background: #fff;
  border: 1px solid #ddd;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background .2s, border-color .2s;
  padding: 0;
}

.btn-icon--more:hover {
  background: #fff;
  border-color: #ccc;
}

.btn-icon--more .icon {
  margin-left: 0;
}

.btn-icon--more svg {
  width: 1.25rem;
  height: 1.25rem;
  stroke: #80878B;
  fill: #80878B;
}
</style>
